import React, { type Ref, forwardRef } from 'react'
import { type InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer'
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary'
import { MentionsPlugin } from '../lexical/plugins/MentionsPlugin'
import type { Profile } from '../user/Profile'
import { MentionNode, ProfilesContext } from '../lexical/nodes/MentionNode'
import { RefPlugin } from '../lexical/plugins/RefPlugin'
import type { LexicalEditor, SerializedEditorState } from 'lexical'
import type { SupportedLanguage } from '../../../shared/i18n/types.no-deps'
import { doNothing } from '../../../shared/lib/functional/function.no-deps'
import { AutoFocusPlugin } from '../lexical/plugins/AutoFocusPlugin'
import { DisablePlugin } from '../lexical/plugins/DisablePlugin'
import classNames from 'classnames'

type Props = {
    profiles: Profile[]
    language: SupportedLanguage
    disabled: boolean
    placeholder?: string
    initialContent?: SerializedEditorState
    mentionsBelow?: boolean
    onUserClick?: (userId: number) => void
    onTaggedUsersChange?: (userIds: number[]) => void
}

export const CommentEditor = forwardRef(
    (
        {
            profiles,
            disabled,
            language,
            initialContent,
            placeholder,
            mentionsBelow,
            onUserClick,
            onTaggedUsersChange,
        }: Props,
        ref: Ref<LexicalEditor>
    ) => {
        const config = buildConfig(disabled, initialContent)

        const onMentionsChange = (mentionNodes: MentionNode[]) =>
            onTaggedUsersChange(mentionNodes.map((node) => node.getUserId()))

        return (
            <div className="AppCore__commentTimeline__CommentEditor">
                <LexicalComposer initialConfig={config}>
                    <ProfilesContext.Provider value={{ profiles, onClick: onUserClick }}>
                        <PlainTextPlugin
                            contentEditable={
                                <ContentEditable
                                    data-testid="comment-input"
                                    className={classNames(
                                        'AppCore__commentTimeline__CommentEditor__ContentEditable',
                                        !disabled && 'AppCore__commentTimeline__CommentEditor__ContentEditable--editing'
                                    )}
                                />
                            }
                            placeholder={<Placeholder text={placeholder} />}
                            ErrorBoundary={LexicalErrorBoundary}
                        />
                        <MentionsPlugin
                            profiles={profiles}
                            language={language}
                            disabled={disabled}
                            below={mentionsBelow}
                            onMentionsChange={onTaggedUsersChange && onMentionsChange}
                        />
                        <RefPlugin ref={ref} />
                        <AutoFocusPlugin />
                        <DisablePlugin disabled={disabled} />
                    </ProfilesContext.Provider>
                </LexicalComposer>
            </div>
        )
    }
)

function Placeholder({ text }: { text?: string }) {
    return text ? <p className="AppCore__commentTimeline__CommentEditor__Placeholder">{text}</p> : null
}

function buildConfig(disabled: boolean, initialContent?: SerializedEditorState): InitialConfigType {
    return {
        namespace: 'CommentEditor',
        nodes: [MentionNode],
        onError: doNothing,
        editable: !disabled,
        editorState: (editor) => {
            if (initialContent) {
                editor.setEditorState(editor.parseEditorState(initialContent))
            }
        },
    }
}
